import { neon, type NeonQueryFunctionInTransaction, type NeonQueryInTransaction } from "@neondatabase/serverless";
import { assertProductionSecrets } from "@/lib/env-guards";

type Row = Record<string, unknown>;
export type Sql = ((strings: TemplateStringsArray, ...values: unknown[]) => Promise<Row[]>) & {
  array?: (...args: unknown[]) => unknown;
};

type NeonSqlFn = ReturnType<typeof neon> & {
  array?: (...args: unknown[]) => unknown;
  unsafe?: (...args: unknown[]) => unknown;
  transaction: ReturnType<typeof neon>["transaction"];
};

function getDbUrl(): string {
  const url = process.env.DATABASE_URL;
  if (!url) throw new Error("DATABASE_URL no configurada");
  return url;
}

let _neon: ReturnType<typeof neon> | null = null;

function getNeon(): ReturnType<typeof neon> {
  if (!_neon) {
    assertProductionSecrets();
    _neon = neon(getDbUrl());
  }
  return _neon;
}

/**
 * Fragmentos SQL anidables (identidad Neon). Usar dentro de `sql()\``… ${sqlFrag()\`AND …\`}\``.
 * Preferible para fragmentos opcionales (`AND …`). `sql()` ya es nestable tras el
 * fix de NeonQueryPromise; sqlFrag sigue siendo la identidad cruda sin bypass.
 */
export function sqlFrag(): NeonSqlFn {
  return getNeon() as NeonSqlFn;
}

/**
 * SQL default = contexto plataforma (app.rls_bypass=1).
 *
 * Tras mig 222 (FORCE RLS), sin bypass el owner también queda sujeto a policies
 * y vería 0 filas. El aislamiento tenant real es `sqlForTenant` (db-tenant-context).
 * Opt-out fail-closed (tests): SM_SQL_DEFAULT_BYPASS=0
 */
export function sql(): Sql {
  const raw = getNeon() as NeonSqlFn;
  if (process.env.SM_SQL_DEFAULT_BYPASS === "0") {
    return raw as unknown as Sql;
  }

  /**
   * Importante: NO envolver en `async () => ...` (Promise nativo).
   * Neon solo anida fragmentos si el valor es `instanceof NeonQueryPromise`
   * (tiene `.queryData`). Un Promise plano se serializa como `$N` →
   * `syntax error at or near $N` / `AND` / `WHERE`.
   *
   * Creamos el thenable con `raw` (nestable) y redirigimos then/catch/finally
   * para ejecutar con `app.rls_bypass=1` al hacer await de primer nivel.
   * Los fragmentos anidados se leen vía `.queryData` sin llamar `.then`.
   */
  const tagged = ((strings: TemplateStringsArray, ...values: unknown[]) => {
    const q = raw(strings, ...values) as Promise<Row[]> & {
      then: Promise<Row[]>["then"];
      catch: Promise<Row[]>["catch"];
      finally: Promise<Row[]>["finally"];
    };

    const runWithBypass = () =>
      (raw.transaction((txn) => [
        txn`SELECT set_config('app.rls_bypass', '1', true)`,
        txn(strings, ...values),
      ]) as Promise<Row[][]>).then((results) => results[1] ?? []);

    q.then = ((onFulfilled?: Parameters<Promise<Row[]>["then"]>[0], onRejected?: Parameters<Promise<Row[]>["then"]>[1]) =>
      runWithBypass().then(onFulfilled, onRejected)) as typeof q.then;
    q.catch = ((onRejected?: Parameters<Promise<Row[]>["catch"]>[0]) =>
      runWithBypass().catch(onRejected)) as typeof q.catch;
    q.finally = ((onFinally?: Parameters<Promise<Row[]>["finally"]>[0]) =>
      runWithBypass().finally(onFinally)) as typeof q.finally;

    return q;
  }) as Sql;

  // Helpers usados por callers legacy (`db.array(...)`).
  if (typeof raw.array === "function") {
    tagged.array = raw.array.bind(raw);
  }

  return tagged;
}

/**
 * Neon HTTP transaction. Default: platform bypass (misma semántica que sql()).
 */
export async function runSqlTransaction(
  build: (txn: NeonQueryFunctionInTransaction<false, false>) => NeonQueryInTransaction[],
): Promise<Row[][]> {
  const rows = (await getNeon().transaction((txn) => {
    const t = txn as NeonQueryFunctionInTransaction<false, false>;
    if (process.env.SM_SQL_DEFAULT_BYPASS === "0") {
      return build(t);
    }
    return [t`SELECT set_config('app.rls_bypass', '1', true)`, ...build(t)];
  })) as Row[][];
  if (process.env.SM_SQL_DEFAULT_BYPASS === "0") return rows;
  return rows.slice(1);
}

/** Driver Neon crudo (tests RLS / set_config manual). */
export function getNeonRaw(): ReturnType<typeof neon> {
  return getNeon();
}

export function nextId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function addDaysIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function addYearsIso(years: number): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().slice(0, 10);
}
